/**
 * Renderer for the server's structured version diff (`ReviewDiff` hunks from
 * `GET .../reviews/{id}/diff`). Pure presentation: per-hunk `@@` header, one
 * row per line with a +/− gutter and dual old/new line-number gutters. No
 * client-side diffing — the hunk JSON is the one canonical shape.
 */
import clsx from "clsx";

import type { ReviewDiff as ReviewDiffData } from "../../api/admin";

export function ReviewDiff({ diff }: { diff: ReviewDiffData }) {
  if (diff.identical) {
    return (
      <p className="text-sm text-ink-low">
        No changes between v{diff.from_version} and v{diff.to_version}.
      </p>
    );
  }

  return (
    <div className="review-diff font-mono text-xs">
      <div className="text-ink-low mb-2 tnum">
        v{diff.from_version} → v{diff.to_version} ·{" "}
        <span className="review-diff-add-count">+{diff.added_lines}</span>{" "}
        <span className="review-diff-del-count">−{diff.removed_lines}</span>
      </div>
      {diff.hunks.map((hunk, hi) => {
        let oldLine = hunk.from_start;
        let newLine = hunk.to_start;
        return (
          <div key={hi} className="review-diff-hunk-wrap">
            <div className="review-diff-hunk tnum">
              @@ -{hunk.from_start},{hunk.from_count} +{hunk.to_start},
              {hunk.to_count} @@
            </div>
            {hunk.lines.map((ln, li) => {
              const oldNo = ln.op !== "add" ? oldLine++ : null;
              const newNo = ln.op !== "del" ? newLine++ : null;
              return (
                <div
                  key={li}
                  className={clsx(
                    "review-diff-line",
                    ln.op === "add" && "review-diff-line--add",
                    ln.op === "del" && "review-diff-line--del",
                  )}
                >
                  <span className="review-diff-gutter tnum">{oldNo ?? ""}</span>
                  <span className="review-diff-gutter tnum">{newNo ?? ""}</span>
                  <span className="review-diff-op">
                    {ln.op === "add" ? "+" : ln.op === "del" ? "−" : " "}
                  </span>
                  <span className="review-diff-text">{ln.text || " "}</span>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
